import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './TeacherTests.css'
import LoadingScreen from '../components/LoadingScreen'
import alert from '../utils/alertService'
import ConfirmModal from '../components/ConfirmModal'

const TeacherTests = () => {
  const [tests, setTests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [testToDelete, setTestToDelete] = useState(null)
  const history = useHistory()

  useEffect(() => {
    let isMounted = true

    const fetchTests = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const response = await fetch('/api/teacher/tests', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          if (isMounted) {
            setTests(data.tests || [])
          }
        } else {
          if (isMounted) {
            setError('Failed to load tests')
          }
        }
      } catch (err) {
        if (isMounted) {
          setError('Network error')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchTests()

    return () => {
      isMounted = false
    }
  }, [])

  const getFilteredTests = () => {
    if (filter === 'all') return tests
    return tests.filter(test => test.status === filter)
  }

  const handleViewTest = (testId) => {
    history.push(`/teacher/test/${testId}`)
  }

  const handleEditTest = (testId) => {
    history.push(`/teacher/test/${testId}/edit`)
  }

  const confirmDeleteTest = (test) => {
    setTestToDelete(test)
    setDeleteModalOpen(true)
  }

  const handleDeleteTest = async () => {
    if (!testToDelete) return
    
    const testId = testToDelete.id
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/teacher/tests/${testId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        setTests(tests.filter(test => test.id !== testId))
        alert.success('Test deleted successfully')
      } else {
        alert.error('Failed to delete test')
      }
    } catch (err) {
      alert.error('Network error')
    }
  }

  if (loading) {
    return <LoadingScreen message="Loading Tests" />
  }

  const filteredTests = getFilteredTests()

  return (
    <div className="teacher-tests-container">
      <header className="page-header">
        <h1>My Tests</h1>
        <div className="header-actions">
          <button onClick={() => history.push('/teacher/create-test')} className="create-button">
            Create New Test
          </button>
          <button onClick={() => history.push('/teacher-dashboard')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="tests-content">
        <div className="filter-section">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Tests ({tests.length})
          </button>
          <button
            className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active ({tests.filter(t => t.status === 'active').length})
          </button>
          <button
            className={`filter-btn ${filter === 'draft' ? 'active' : ''}`}
            onClick={() => setFilter('draft')}
          >
            Draft ({tests.filter(t => t.status === 'draft').length})
          </button>
          <button
            className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Completed ({tests.filter(t => t.status === 'completed').length})
          </button>
        </div>

        <div className="tests-grid">
          {filteredTests.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <h3>No Tests Found</h3>
              <p>Create your first test to get started</p>
              <button onClick={() => history.push('/teacher/create-test')} className="primary-btn">
                Create Test
              </button>
            </div>
          ) : (
            filteredTests.map((test) => (
              <div key={test.id} className="test-card">
                <div className="test-card-header">
                  <h3>{test.title}</h3>
                  <span className={`status-badge status-${test.status}`}>
                    {test.status}
                  </span>
                </div>
                <p className="test-description">{test.description}</p>
                <div className="test-stats">
                  <div className="stat">
                    <span className="stat-label">Duration</span>
                    <span className="stat-value">{test.duration_minutes || 0} min</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Total Points</span>
                    <span className="stat-value">{test.total_points || 0}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Attempts</span>
                    <span className="stat-value">{test.total_attempts || 0}</span>
                  </div>
                </div>
                <div className="test-card-actions">
                  <button 
                    className="action-btn view-btn"
                    onClick={() => handleViewTest(test.id)}
                  >
                    View
                  </button>
                  <button 
                    className="action-btn edit-btn"
                    onClick={() => handleEditTest(test.id)}
                  >
                    Edit
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => confirmDeleteTest(test)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setTestToDelete(null)
        }}
        onConfirm={handleDeleteTest}
        title="Delete Test"
        message={`Are you sure you want to delete "${testToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        isDangerous={true}
      />
    </div>
  )
}

export default TeacherTests
